/* eslint-disable operator-linebreak */
import { ReactElement, useState, useEffect } from 'react';
import { tile, iNominationInfoDisplay } from '@interfaces/interfaces';
import { Field, useFormikContext, getIn } from 'formik';
import { TextInput, DateInput, LicenceFieldAccessible, CheckboxInputAccessible, TileRadioAccessible, EmailFieldWithExperian, ContactInputWithExperian, Alert, AddressFieldAccessible } from '@components';
import { iNominationFormValues } from '@services/NominationService';

import gridStyles from '@pages/forms/Grid.module.scss';
import styles from './IndividualFormNominate.module.scss';
import InfoIcon from '../../../images/info.svg';

interface iIndividualFormNominate {
  displayObject: iNominationInfoDisplay;
  currentStepNo: string;
}

function IndividualFormNominate({ displayObject, currentStepNo }: iIndividualFormNominate): ReactElement {
  const formikContext = useFormikContext();
  const [isDisabled, setDisabled] = useState(false);
  const { values }: { values: iNominationFormValues } = useFormikContext();
  // const { pageTitle, pageSubtitle } = displayObject.pages.contactInformation[currentStepNo];
  const fieldsOnSteps = displayObject.pages.personalContactInformation[currentStepNo].fields;
  const isStep6 = currentStepNo === 'step6';
  const isStep11 = currentStepNo === 'step11';
  const YesValue = (): ReactElement => (
    <>
      <div>Yes</div>
    </>
  );

  const NoValue = (): ReactElement => (
    <>
      <div>No</div>
    </>
  );
  const WarningMessage = (): ReactElement => (
    <>
      <p>Is this correct? You have already told us who was driving - now we need details for the person named on the fine.</p>
    </>
  );

  const TILE_DATA: tile[] = [
    {
      body: <YesValue />,
      value: 'true',
    },
    {
      body: <NoValue />,
      value: 'false',
    },
  ];
  const [disableDOB, setDisableDOB] = useState(false);

  const differentToPostal: tile[] = [
    {
      body: <YesValue />,
      value: 'true',
    },
    {
      body: <NoValue />,
      value: 'false',
    },
  ];
  const getFieldValueBasedOnStep = (): any[] => {
    if (isStep6) {
      return [];
    }
    if (currentStepNo === 'step11') {
      return [
        getIn(values, fieldsOnSteps.givenName.formikName),
        getIn(values, fieldsOnSteps.familyName.formikName),
      ];
    }
    return [];
  };

  useEffect(() => {
    if (!isStep11) {
      if (getIn(values, fieldsOnSteps.dateOfBirthYesNo.formikName)) {
        setDisableDOB(true);
      } else {
        setDisableDOB(false);
      }
    }
  }, [getIn(values, fieldsOnSteps.dateOfBirthYesNo?.formikName)]);

  useEffect(() => {
    /* Step6 conditions */
    /* Step15 conditions */
    if (currentStepNo === 'step15') {
      /* General conditions */
      if ((getIn(formikContext.values, fieldsOnSteps.noResidentialAddress.formikName) || getIn(formikContext.values, fieldsOnSteps.noFixedAddress.formikName)) && getIn(formikContext.values, fieldsOnSteps.deliveryAddressType.formikName) === 'ResidentialAddress') {
        formikContext.setFieldValue(fieldsOnSteps.deliveryAddressType.formikName, '');
        formikContext.setFieldTouched(fieldsOnSteps.deliveryAddressType.formikName);
      } // This was missing
      if (getIn(values, fieldsOnSteps.noFixedAddress.formikName) || getIn(values, fieldsOnSteps.noResidentialAddress.formikName)) {
        setDisabled(true);
      } else {
        setDisabled(false);
      }
    }
  }, [
    ...getFieldValueBasedOnStep(),
    currentStepNo,
  ]);
  return (
    <>
      <div className={gridStyles.form}>
        <div className={gridStyles.gridColFull}>
          <Field component={TextInput} name={fieldsOnSteps.givenName.formikName} title={fieldsOnSteps.givenName.title} isMandatory />
        </div>
        <div className={gridStyles.gridColFull}>
          <Field component={TextInput} name={fieldsOnSteps.familyName.formikName} title={fieldsOnSteps.familyName.title} isMandatory />
        </div>
        {isStep11 && values.GivenNamesYourDetails && values.FamilyNameYourDetails && (values.GivenNamesSomeoneElse || values.GivenNamesSoldDisposed) === values.GivenNamesYourDetails && (values.FamilyNameSomeoneElse || values.FamilyNameSoldDisposed) === values.FamilyNameYourDetails && (
          <div className={gridStyles.gridColFull}>
            <Alert
              icon={InfoIcon}
              message={<WarningMessage />}
              theme="info2"
              classAlt={styles.boxheight}
              color="default"
            />
          </div>
        )}
        <div className={gridStyles.gridColFull}>
          <LicenceFieldAccessible
            title={fieldsOnSteps.driverLicence.title}
            name={fieldsOnSteps.driverLicence.formikName}
            nameStateOfIssue={fieldsOnSteps.licenceState.formikName}
            nameCountryOfIssue={fieldsOnSteps.statecountryOfIssue.formikName}
            nameNoDriverLicence={fieldsOnSteps.noDriverLicence.formikName}
            noDriverLicenceTitle={fieldsOnSteps.noDriverLicence.title}
            isMandatory
          />
        </div>
        {isStep6 && (
          <div className={gridStyles.gridColFull}>
            <p>
              You should provide
              {' '}
              <strong>either licence number or date of birth</strong>
              , or your nomination may be unsuccessful.
            </p>
          </div>
        )}
        <div className={gridStyles.gridColHalf}>
          <Field name={fieldsOnSteps.dateOfBirth.formikName} component={DateInput} minDate={new Date(1900, 1, 1)} title={fieldsOnSteps.dateOfBirth.title} isMandatory maxDate={new Date()} isDisabled={disableDOB} />
        </div>
        <div className={gridStyles.gridColFull}>
          {!isStep11 && (
            <Field
              name={fieldsOnSteps.dateOfBirthYesNo.formikName}
              component={CheckboxInputAccessible}
              title={fieldsOnSteps.dateOfBirthYesNo.title}
              boxChecked={(e: boolean) => {
                setDisableDOB(e);
                formikContext.setFieldTouched(fieldsOnSteps.dateOfBirth.formikName, false);
                formikContext.setFieldValue(fieldsOnSteps.dateOfBirth.formikName, '');
              }}
            />
          )}
          {isStep6 && (
            <>
              <Field
                name={fieldsOnSteps.taxiDriverAccNumber.formikName}
                title={fieldsOnSteps.taxiDriverAccNumber.title}
                component={TextInput}
              />
              <p>If the offence involves a taxi, it is recommended that taxi operator records are also provided, to confirm the nominee was driving.</p>
            </>
          )}
        </div>
        {/* Postal address */}
        {isStep11 && (
          <>
            <div className={gridStyles.gridColFull}>
              <AddressFieldAccessible name={fieldsOnSteps.postalAddress.formikName} title={fieldsOnSteps.postalAddress.title} manualAddressText="Enter address manually" isMandatory />
            </div>

            <div className={gridStyles.gridColFull}>
              <Field
                component={TileRadioAccessible}
                name={fieldsOnSteps.differentToPostal.formikName}
                title={fieldsOnSteps.differentToPostal.title}
                tileData={differentToPostal}
                tileContainer
                isMandatory
                orientationRow
                classname={styles.tile}
              />
            </div>
          </>
        )}
        {(currentStepNo !== 'step11' || getIn(values, fieldsOnSteps.differentToPostal.formikName) === 'true') &&
          (
            <div className={gridStyles.gridColFull}>
              <AddressFieldAccessible
                name={fieldsOnSteps.residentialAddress.formikName}
                title={fieldsOnSteps.residentialAddress.title}
                manualAddressText="Enter address manually"
                isMandatory
                disabled={isDisabled}
              />
            </div>
          )}
        {currentStepNo === 'step15' && (
          <div className={gridStyles.gridColFull}>
            <Field component={TileRadioAccessible} name={fieldsOnSteps.isEmailRequired.formikName} title={fieldsOnSteps.isEmailRequired.title} tileData={TILE_DATA} tileContainer isMandatory orientationRow />
          </div>
        )}
        <div className={gridStyles.gridColFull}>
          <Field component={ContactInputWithExperian} name={fieldsOnSteps.individualContactNumber.formikName} title="Contact phone number" isMandatory orientationRow obligationNumber={values.OBLIGATION_NUMBER} formName={displayObject.mode} secondaryContact={false} hasPrimaryNumberType={false} noContactFieldRequired={!isStep11} noContactFieldName={fieldsOnSteps.noContactNumber.formikName} currentSP={fieldsOnSteps.noContactNumber.title} warningMessage={fieldsOnSteps.individualContactNumberWarning.formikName} />
          <Field component={EmailFieldWithExperian} name={fieldsOnSteps.email.formikName} title="Email" isMandatory orientationRow obligationNumber={values.OBLIGATION_NUMBER} formName={displayObject.mode} noEmailField={fieldsOnSteps.noEmail.formikName} currentSP={fieldsOnSteps.noEmail.title} warningMessage={fieldsOnSteps.emailWarning.formikName} />
        </div>
      </div>
    </>
  );
}
export default IndividualFormNominate;
